/**
 * Oeffentliche Bewertungsseite (Spec 8.6) — **ohne Login**.
 *
 * Die einzige Seite des Admin-Bundles, die ein Endkunde je sieht. Deshalb ist
 * sie bewusst karg: Ticketnummer, Betreff, fuenf Knoepfe, ein Kommentarfeld,
 * danach eine Danke-Zeile. Kein Verlauf, keine Kontaktdaten, keine Navigation
 * ins System.
 *
 * Token und Signatur stehen in der URL (`/csat/:token?s=…`); geprueft werden
 * sie ausschliesslich auf dem Server.
 */
import { CSAT_MAX_SCORE, CSAT_MIN_SCORE, type PublicCsat } from '@tickethub/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, ApiError } from '@/lib/api';
import { apiPaths } from '@/lib/api-paths';

const SCORES = Array.from(
  { length: CSAT_MAX_SCORE - CSAT_MIN_SCORE + 1 },
  (_, index) => CSAT_MIN_SCORE + index,
);

export function CsatPage() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const signature = params.get('s') ?? '';

  const [view, setView] = useState<PublicCsat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  /** Signatur haengt an jedem Aufruf; der Pfad selbst kommt aus `lib/api-paths.ts`. */
  const query = `?s=${encodeURIComponent(signature)}`;

  const load = useCallback(async () => {
    try {
      setView(await api.get<PublicCsat>(apiPaths.publicCsat(token) + query));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('csat.public_invalid'));
    }
  }, [token, query, t]);

  useEffect(() => {
    // `load` setzt State erst *nach* dem Netzwerk-Await — es entsteht keine
    // kaskadierende Render-Schleife. Die Regel kann das nicht unterscheiden.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void load();
  }, [load]);

  async function submit(score: number) {
    setBusy(true);
    try {
      setView(
        await api.post<PublicCsat>(apiPaths.publicCsat(token) + query, {
          score,
          comment: comment.trim() || null,
        }),
      );
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('csat.public_invalid'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-xl flex-col justify-center gap-6 p-6">
      {error !== null ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : view === null ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : view.answered ? (
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold">{t('csat.public_thanks_title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('csat.public_thanks_body', { score: view.score ?? '', max: CSAT_MAX_SCORE })}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="space-y-1 text-center">
            <p className="text-xs text-muted-foreground">{view.tenant_name}</p>
            <h1 className="text-2xl font-semibold">{t('csat.public_title')}</h1>
            <p className="text-sm text-muted-foreground">
              <span className="font-mono text-xs">{view.ticket_number}</span> — {view.subject}
            </p>
          </div>

          <div className="flex justify-center gap-2">
            {SCORES.map((score) => (
              <Button
                key={score}
                type="button"
                variant="outline"
                size="lg"
                disabled={busy}
                aria-label={t('csat.score', { score, max: CSAT_MAX_SCORE })}
                onClick={() => void submit(score)}
              >
                {score}
              </Button>
            ))}
          </div>

          <div className="space-y-1">
            <Label htmlFor="csat-comment">{t('csat.public_comment')}</Label>
            <Input
              id="csat-comment"
              value={comment}
              maxLength={2000}
              placeholder={t('csat.public_comment_placeholder')}
              onChange={(event) => setComment(event.target.value)}
            />
          </div>
        </div>
      )}
    </main>
  );
}
